import { Box, Fade, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Container, Heading } from '../atomic';

/** Edition pill labels for the EllaFin Editions row */
const editionPills = [
  'Nonprofits, Foundations & Mission Driven Organizations',
  'Healthcare Biotech & Pharma, Research Schools & Hospitals',
  'Consumer Package Goods & Retail',
];

/**
 * Product Hero Section
 * Centered layout with dark navy background, headline/subtitle,
 * and a cohesive EllaFin Editions strip.
 *
 * @returns {JSX.Element} Product hero section with chart grid and ticker strip
 */
function ProductHero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      <Box
        component="section"
        aria-label="Product hero"
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '85vh' },
          py: { xs: 6, md: 0 },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundColor: theme.custom.footerBackground || '#13152b',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              py: { xs: 3, md: 6 },
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* Headline */}
            <Fade in={isLoaded} timeout={600}>
              <Box sx={{ mb: { xs: 0, md: 1 } }}>
                <Heading
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: '2.25rem',
                      sm: '2.75rem',
                      md: '3.25rem',
                      lg: '3.75rem',
                    },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                    mb: 1.5,
                  }}
                >
                  Finance-First{' '}
                  <span style={{ color: theme.palette.secondary.main }}>
                    Fin
                  </span>
                  Ops
                </Heading>

                {/* Subtitle */}
                <Typography
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: theme.palette.secondary.main,
                    fontStyle: 'italic',
                    lineHeight: 1.6,
                    maxWidth: '700px',
                    mx: 'auto',
                  }}
                >
                  One Business Intelligence Layer. Three Financial Experiences.
                </Typography>
              </Box>
            </Fade>

            {/* 3-Panel Application Demo — center panel wider, sides overlap */}
            <Fade in={isLoaded} timeout={900}>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'flex' },
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '1200px',
                  mx: 'auto',
                  px: { xs: 1, md: 0 },
                  position: 'relative',
                }}
              >
                {/* Panel 1 wrapper: card + label below */}
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '0 0 30%' },
                    marginRight: { xs: 0, md: '-24px' },
                    zIndex: 0,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
                        zIndex: 2,
                      },
                    }}
                  >
                    {/* Title bar with EllaFin logo */}
                    <Box
                      sx={{
                        backgroundColor: '#1e2140',
                        px: 1.5,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: '#ff5f57',
                          }}
                        />
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: '#febc2e',
                          }}
                        />
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: '#28c840',
                          }}
                        />
                      </Box>
                      <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/images/full-logo-white-green.png`}
                        alt=""
                        aria-hidden="true"
                        sx={{ height: 14 }}
                      />
                    </Box>
                    {/* Tab bar */}
                    <Box
                      sx={{
                        display: 'flex',
                        borderBottom: '2px solid #e0e0e0',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          color: '#1e2140',
                          borderBottom: '2px solid #779a0a',
                          mb: '-2px',
                        }}
                      >
                        Donor Engagement
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          fontSize: '0.55rem',
                          color: '#999',
                        }}
                      >
                        Connect Cost Tracker
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          fontSize: '0.55rem',
                          color: '#999',
                        }}
                      >
                        Cloud Spend Efficiency
                      </Box>
                    </Box>
                    {/* Content */}
                    <Box sx={{ p: 2, flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: '#1e2140',
                          mb: 0.5,
                        }}
                      >
                        Donor Engagement &amp; Donations
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.55rem', color: '#999', mb: 1.5 }}
                      >
                        6-month performance overview &middot; Sep 2025 – Feb
                        2026
                      </Typography>
                      {/* KPI row */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {[
                          { label: 'Total Donor Calls', value: '1,847' },
                          { label: 'Total Donations', value: '$142,500' },
                          { label: 'Avg Calls / Month', value: '308' },
                          { label: 'Avg Donation / Mo', value: '$23,750' },
                        ].map((kpi) => (
                          <Box
                            key={kpi.label}
                            sx={{
                              border: '1px solid #e8e8e8',
                              borderRadius: '8px',
                              p: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.5rem',
                                color: '#999',
                                mb: 0.25,
                              }}
                            >
                              {kpi.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#1e2140',
                              }}
                            >
                              {kpi.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {/* Chart placeholder */}
                      <Box
                        sx={{
                          backgroundColor: '#f8f8f8',
                          borderRadius: '8px',
                          p: 1.5,
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: '#1e2140',
                            mb: 1,
                          }}
                        >
                          Donor Calls vs Donation Revenue
                        </Typography>
                        {/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                        <svg
                          viewBox="0 0 280 100"
                          width="100%"
                          aria-label="Line chart placeholder"
                        >
                          {/* Grid lines */}
                          {[0, 25, 50, 75, 100].map((y) => (
                            <line
                              key={y}
                              x1="30"
                              y1={y}
                              x2="270"
                              y2={y}
                              stroke="#e8e8e8"
                              strokeWidth="0.5"
                            />
                          ))}
                          {/* Calls line (navy) */}
                          <polyline
                            points="30,85 80,70 130,58 180,48 230,40 270,28"
                            fill="none"
                            stroke="#1e2140"
                            strokeWidth="2"
                          />
                          {/* Donations line (green) */}
                          <polyline
                            points="30,78 80,65 130,52 180,42 230,35 270,30"
                            fill="none"
                            stroke="#779a0a"
                            strokeWidth="2"
                            strokeDasharray="4,2"
                          />
                          {/* X labels */}
                          {['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map(
                            (m, i) => (
                              <text
                                key={m}
                                x={30 + i * 48}
                                y="98"
                                fontSize="5"
                                fill="#999"
                                textAnchor="middle"
                              >
                                {m}
                              </text>
                            )
                          )}
                        </svg>
                      </Box>
                      {/* Table placeholder */}
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: '#1e2140',
                          mb: 0.5,
                        }}
                      >
                        Monthly Breakdown
                      </Typography>
                      <Box sx={{ fontSize: '0.45rem' }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 0.5,
                            pb: 0.5,
                            borderBottom: '1px solid #e8e8e8',
                            color: '#999',
                            fontWeight: 600,
                          }}
                        >
                          <span>Month</span>
                          <span>Calls</span>
                          <span>MoM%</span>
                          <span>Donations</span>
                          <span>MoM%</span>
                          <span>Avg/Call</span>
                        </Box>
                        {[
                          {
                            month: 'Sep 2025',
                            calls: '185',
                            mom1: '—',
                            donations: '$15,200',
                            mom2: '—',
                            avg: '$82.16',
                          },
                          {
                            month: 'Oct 2025',
                            calls: '248',
                            mom1: '+34.1%',
                            donations: '$19,800',
                            mom2: '+30.3%',
                            avg: '$79.84',
                          },
                          {
                            month: 'Nov 2025',
                            calls: '295',
                            mom1: '+19.0%',
                            donations: '$23,400',
                            mom2: '+18.2%',
                            avg: '$79.32',
                          },
                          {
                            month: 'Dec 2025',
                            calls: '332',
                            mom1: '+12.5%',
                            donations: '$26,100',
                            mom2: '+11.5%',
                            avg: '$78.61',
                          },
                          {
                            month: 'Jan 2026',
                            calls: '368',
                            mom1: '+10.8%',
                            donations: '$28,500',
                            mom2: '+9.2%',
                            avg: '$77.45',
                          },
                          {
                            month: 'Feb 2026',
                            calls: '419',
                            mom1: '+13.9%',
                            donations: '$29,500',
                            mom2: '+3.5%',
                            avg: '$70.41',
                          },
                        ].map((row, i) => (
                          <Box
                            key={row.month}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(6, 1fr)',
                              gap: 0.5,
                              py: 0.3,
                              backgroundColor:
                                i % 2 === 1 ? '#f9f9f9' : 'transparent',
                              color: '#333',
                            }}
                          >
                            <span>{row.month}</span>
                            <span>{row.calls}</span>
                            <span
                              style={{
                                color: row.mom1 !== '—' ? '#779a0a' : '#999',
                              }}
                            >
                              {row.mom1}
                            </span>
                            <span>{row.donations}</span>
                            <span
                              style={{
                                color: row.mom2 !== '—' ? '#779a0a' : '#999',
                              }}
                            >
                              {row.mom2}
                            </span>
                            <span>{row.avg}</span>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontStyle: 'italic',
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      mt: 1,
                    }}
                  >
                    EllaFin&trade; Nonprofit Edition
                  </Typography>
                </Box>

                {/* Panel 2: Amazon Connect ROI (center, larger, on top) */}
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 16px 56px rgba(0,0,0,0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: { xs: '1 1 100%', md: '0 0 44%' },
                    zIndex: 1,
                    position: 'relative',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 64px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  {/* Title bar */}
                  <Box
                    sx={{
                      backgroundColor: '#1e2140',
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#ff5f57',
                        }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#febc2e',
                        }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#28c840',
                        }}
                      />
                    </Box>
                    <Box
                      component="img"
                      src={`${process.env.PUBLIC_URL}/images/full-logo-white-green.png`}
                      alt=""
                      aria-hidden="true"
                      sx={{ height: 16 }}
                    />
                  </Box>
                  {/* Tab bar */}
                  <Box
                    sx={{ display: 'flex', borderBottom: '2px solid #e0e0e0' }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        fontSize: '0.55rem',
                        color: '#999',
                      }}
                    >
                      Donor Engagement
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        fontSize: '0.55rem',
                        color: '#999',
                      }}
                    >
                      Connect Cost Tracker
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        color: '#1e2140',
                        borderBottom: '2px solid #779a0a',
                        mb: '-2px',
                      }}
                    >
                      Connect ROI
                    </Box>
                  </Box>
                  {/* Content */}
                  <Box sx={{ p: 2, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: '#1e2140',
                        mb: 0.5,
                      }}
                    >
                      Amazon Connect ROI — Cost vs. Donor Impact
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.55rem', color: '#999', mb: 1.5 }}
                    >
                      6-month platform cost analysis &middot; Sep 2025 – Feb
                      2026
                    </Typography>
                    {/* KPI row */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      {[
                        { label: 'Monthly Platform Cost', value: '$175' },
                        {
                          label: 'Monthly Donations Generated',
                          value: '$23,833',
                        },
                        { label: 'ROI', value: '136x return' },
                        { label: 'Cost Per Donation $', value: '$0.007' },
                      ].map((kpi) => (
                        <Box
                          key={kpi.label}
                          sx={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                            p: 1,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.45rem',
                              color: '#999',
                              mb: 0.25,
                            }}
                          >
                            {kpi.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize:
                                kpi.label === 'ROI' ? '0.7rem' : '0.75rem',
                              fontWeight: 700,
                              color:
                                kpi.label === 'ROI' ? '#779a0a' : '#1e2140',
                            }}
                          >
                            {kpi.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {/* Stacked bar chart */}
                    <Box
                      sx={{
                        backgroundColor: '#f8f8f8',
                        borderRadius: '8px',
                        p: 1.5,
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: '#1e2140',
                          mb: 1,
                        }}
                      >
                        Monthly Donations vs. Platform Cost
                      </Typography>
                      {/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                      <svg
                        viewBox="0 0 280 90"
                        width="100%"
                        aria-label="Stacked bar chart placeholder"
                      >
                        {/* Donation bars (green, tall) */}
                        {[40, 52, 60, 66, 72, 75].map((h, i) => (
                          <rect
                            key={`d-${i}`}
                            x={35 + i * 42}
                            y={80 - h}
                            width="20"
                            height={h}
                            rx="2"
                            fill="#779a0a"
                          />
                        ))}
                        {/* Cost bars (navy, proportional to actual costs) */}
                        {[
                          { h: 2.7, y: 77.3 },
                          { h: 4.7, y: 75.3 },
                          { h: 7.2, y: 72.8 },
                          { h: 7.6, y: 72.4 },
                          { h: 7.7, y: 72.3 },
                          { h: 8, y: 72 },
                        ].map((bar, i) => (
                          <rect
                            key={`c-${i}`}
                            x={57 + i * 42}
                            y={bar.y}
                            width="8"
                            height={bar.h}
                            rx="1"
                            fill="#1e2140"
                          />
                        ))}
                        {/* X labels */}
                        {['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map(
                          (m, i) => (
                            <text
                              key={m}
                              x={49 + i * 42}
                              y="88"
                              fontSize="5"
                              fill="#999"
                              textAnchor="middle"
                            >
                              {m}
                            </text>
                          )
                        )}
                      </svg>
                    </Box>
                    {/* Key Insight */}
                    <Box
                      sx={{
                        backgroundColor: '#f0f5e0',
                        borderRadius: '8px',
                        p: 1.5,
                        borderLeft: `3px solid #779a0a`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          color: '#1e2140',
                          mb: 0.25,
                        }}
                      >
                        ✦ Key Insight
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.5rem',
                          color: '#555',
                          lineHeight: 1.5,
                        }}
                      >
                        For every $1 spent on Amazon Connect, EllaFin™ generated
                        approximately $136 in donor contributions over the past
                        6 months.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Panel 3: Amazon Connect Cost Tracker (right, smaller, overlaps center) */}
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: { xs: '1 1 100%', md: '0 0 30%' },
                    marginLeft: { xs: 0, md: '-24px' },
                    marginTop: { xs: 0, md: '-60px' },
                    zIndex: 0,
                    position: 'relative',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
                      zIndex: 2,
                    },
                  }}
                >
                  {/* Title bar with EllaFin logo */}
                  <Box
                    sx={{
                      backgroundColor: '#1e2140',
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: '#ff5f57',
                        }}
                      />
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: '#febc2e',
                        }}
                      />
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: '#28c840',
                        }}
                      />
                    </Box>
                    <Box
                      component="img"
                      src={`${process.env.PUBLIC_URL}/images/full-logo-white-green.png`}
                      alt=""
                      aria-hidden="true"
                      sx={{ height: 14 }}
                    />
                  </Box>
                  {/* Content */}
                  <Box sx={{ p: 2, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: '#1e2140',
                        mb: 0.5,
                      }}
                    >
                      Amazon Connect Cost Tracker
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.55rem', color: '#999', mb: 1.5 }}
                    >
                      6-month cloud telephony cost analysis &middot; Sep 2025 –
                      Feb 2026
                    </Typography>
                    {/* KPI row */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      {[
                        { label: 'Monthly Cost', value: '$175.00' },
                        { label: '6-Month Total', value: '$1,050.00' },
                        { label: 'Cost Trend', value: 'Rising' },
                      ].map((kpi) => (
                        <Box
                          key={kpi.label}
                          sx={{
                            border: '1px solid #e8e8e8',
                            borderRadius: '8px',
                            p: 1,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.5rem', color: '#999', mb: 0.25 }}
                          >
                            {kpi.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              color: '#1e2140',
                            }}
                          >
                            {kpi.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {/* Bar chart */}
                    <Box
                      sx={{
                        backgroundColor: '#f8f8f8',
                        borderRadius: '8px',
                        p: 1.5,
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: '#1e2140',
                          mb: 1,
                        }}
                      >
                        Monthly Amazon Connect Cost
                      </Typography>
                      {/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                      <svg
                        viewBox="0 0 280 90"
                        width="100%"
                        aria-label="Bar chart placeholder"
                      >
                        {[
                          { h: 18.6, label: 'Sep' },
                          { h: 32.2, label: 'Oct' },
                          { h: 49.5, label: 'Nov' },
                          { h: 52, label: 'Dec' },
                          { h: 52.8, label: 'Jan' },
                          { h: 55, label: 'Feb' },
                        ].map((bar, i) => (
                          <g key={i}>
                            <rect
                              x={35 + i * 42}
                              y={75 - bar.h}
                              width="28"
                              height={bar.h}
                              rx="3"
                              fill="#1e2140"
                            />
                            <text
                              x={49 + i * 42}
                              y="88"
                              fontSize="5"
                              fill="#999"
                              textAnchor="middle"
                            >
                              {bar.label}
                            </text>
                          </g>
                        ))}
                        {/* Y labels */}
                        <text
                          x="25"
                          y="24"
                          fontSize="4.5"
                          fill="#999"
                          textAnchor="end"
                        >
                          $250
                        </text>
                        <text
                          x="25"
                          y="48"
                          fontSize="4.5"
                          fill="#999"
                          textAnchor="end"
                        >
                          $125
                        </text>
                        <text
                          x="25"
                          y="76"
                          fontSize="4.5"
                          fill="#999"
                          textAnchor="end"
                        >
                          $0
                        </text>
                      </svg>
                    </Box>
                    {/* Combined chart */}
                    <Box
                      sx={{
                        backgroundColor: '#f8f8f8',
                        borderRadius: '8px',
                        p: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          color: '#1e2140',
                          mb: 1,
                        }}
                      >
                        ↘ Cost Per Donor Call (Trending Down)
                      </Typography>
                      {/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                      <svg
                        viewBox="0 0 280 90"
                        width="100%"
                        aria-label="Combined chart placeholder"
                      >
                        {/* Bars - donor calls increasing */}
                        {[30, 42, 50, 56, 62, 70].map((h, i) => (
                          <rect
                            key={i}
                            x={35 + i * 42}
                            y={80 - h}
                            width="28"
                            height={h}
                            rx="3"
                            fill="#9ab43a"
                            opacity="0.7"
                          />
                        ))}
                        {/* Line - cost per call decreasing */}
                        <polyline
                          points="49,15 91,28 133,38 175,45 217,48 259,50"
                          fill="none"
                          stroke="#1e2140"
                          strokeWidth="2"
                        />
                        {[15, 28, 38, 45, 48, 50].map((y, i) => (
                          <circle
                            key={i}
                            cx={49 + i * 42}
                            cy={y}
                            r="2.5"
                            fill="#1e2140"
                          />
                        ))}
                        {/* X labels */}
                        {['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map(
                          (m, i) => (
                            <text
                              key={m}
                              x={49 + i * 42}
                              y="88"
                              fontSize="5"
                              fill="#999"
                              textAnchor="middle"
                            >
                              {m}
                            </text>
                          )
                        )}
                      </svg>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* EllaFin Editions Strip -- full width */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: alpha(
            theme.custom.footerBackground || '#13152b',
            0.95
          ),
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Container maxWidth="lg">
          {/* Top row: Logo + Editions on left, Nonprofit Edition on right */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: { xs: 1, md: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/full-logo-white-green.png`}
                alt="EllaFin"
                sx={{
                  height: { xs: 35, md: 45 },
                  display: 'block',
                }}
              />
              <Typography
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                }}
              >
                Editions
              </Typography>
            </Box>
          </Box>

          {/* Edition Pills -- single centered row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: 'center',
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              gap: { xs: 1, md: 1.5 },
            }}
          >
            {editionPills.map((label) => (
              <Box
                key={label}
                sx={{
                  border: `1.5px solid ${theme.palette.secondary.main}`,
                  borderRadius: '9999px',
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 0.75, md: 0.85 },
                  color: '#ffffff',
                  fontSize: { xs: '0.75rem', md: '0.85rem' },
                  fontWeight: 500,
                  lineHeight: 1.3,
                  textAlign: 'center',
                  whiteSpace: isMobile ? 'normal' : 'nowrap',
                  width: { xs: '100%', sm: 'auto' },
                  flex: { sm: '1 1 0', md: '0 1 auto' },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default ProductHero;
